import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";

type Props = {
  open: boolean;
  amount: number;
  busy?: boolean;
  onConfirm: (note: string, flatRate: boolean) => void;
  onCancel: () => void;
};

const fmt = (n: number) => `$${n.toFixed(2)}`;

const ConfirmBidModal: React.FC<Props> = ({
  open,
  amount,
  busy,
  onConfirm,
  onCancel,
}) => {
  const [note, setNote] = React.useState("");
  const [flatRate, setFlatRate] = React.useState(false);
  const maxLen = 1000;

  // prevent closing while request is in-flight
  const handleClose = () => {
    if (!busy) onCancel();
  };

  const handleConfirm = () => {
    if (!busy) onConfirm(note.trim(), flatRate);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        {/* Centered panel */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-150"
            enterFrom="opacity-0 translate-y-2 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-xl bg-white shadow-lg ring-1 ring-gray-200">
              <div className="px-5 pt-5 pb-3">
                <Dialog.Title className="text-lg font-semibold">
                  Confirm your bid
                </Dialog.Title>
                <p className="mt-2 text-sm text-gray-600">
                  You’re about to place a bid of{" "}
                  <span className="font-semibold text-gray-900">
                    {fmt(amount)}
                  </span>
                  .
                </p>

                {/* Details / notes */}
                <div className="mt-4">
                  <label
                    htmlFor="bid-note"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Details (optional)
                  </label>
                  <textarea
                    id="bid-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={maxLen}
                    rows={4}
                    placeholder="Add any details for the seller (timing, special handling, etc.)"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <div className="mt-1 text-xs text-gray-500 text-right">
                    {note.length}/{maxLen}
                  </div>

                  <label className="inline-flex items-center gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300"
                      checked={flatRate}
                      onChange={(e) => setFlatRate(e.target.checked)}
                    />
                    Flat rate (total price, not per unit)
                  </label>
                </div>
              </div>

              <div className="px-5 py-3 bg-gray-50 rounded-b-xl flex items-center justify-end gap-2">
                <button
                  className="px-4 py-2 rounded-md text-sm border border-gray-300 hover:bg-gray-100 disabled:opacity-60"
                  onClick={onCancel}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-md text-sm bg-primary text-white hover:bg-primary-dark disabled:opacity-60"
                  onClick={handleConfirm}
                  disabled={busy}
                >
                  {busy ? "Placing…" : "Confirm Bid"}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ConfirmBidModal;
